import type { MenuItem } from "@v1/components/designSystem/menu/Menu";
import type { ComponentTemplate } from "@v1/types/component-template";
import type { ComponentTemplateIndustryId } from "schemas/componentTemplates";
import type { TemplateDevices } from "./ChooseTemplateOverlay";

import React from "react";

import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { Menu, MenuTrigger } from "@v1/components/designSystem/menu/Menu";
import { getOptimizedImageUrl } from "@v1/utils/component-template";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { useModal } from "@v2/store/modalStore";
import { Trash } from "lucide-react";
import { getComponentTemplateIndustryName } from "replo-runtime/shared/componentTemplates";

import { ComponentTemplateSnapshot } from "../component-template/ComponentTemplateSnapshot";
import CopyTemplateLinkButton from "./CopyTemplateLinkButton";

interface TemplateCardProps {
  template: ComponentTemplate;
  setSubpageTemplate: (template: ComponentTemplate | null) => void;
  previewPlatform: TemplateDevices;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  setSubpageTemplate,
  previewPlatform,
}) => {
  const analytics = useLogAnalytics();
  const modal = useModal();

  const menuItems: MenuItem[] = [
    {
      id: "deleteTemplate",
      type: "leaf" as const,
      title: "Delete",
      variant: "default" as const,
      size: "sm" as const,
      startEnhancer: <Trash size={12} />,
      onSelect: () => {
        modal.openModal({
          type: "deleteComponentTemplateConfirmationModal",
          props: {
            template: template,
          },
        });
      },
    },
  ];

  const handleClick = () => {
    analytics("editor.marketplace.details", {
      type: "componentTemplate",
      componentTemplateId: template.id,
      componentTemplateName: template.name,
    });

    setSubpageTemplate(template);
  };

  const industryName = template.industryId
    ? getComponentTemplateIndustryName(
        template.industryId as ComponentTemplateIndustryId,
      )
    : null;

  // Get optimized logo URL
  const optimizedLogoUrl = template.logoUrl
    ? getOptimizedImageUrl(template.logoUrl, { width: 100, fit: "contain" })
    : null;

  return (
    <Menu
      menuType="context"
      items={menuItems}
      customWidth={200}
      trigger={
        <MenuTrigger asChild>
          <button
            className="hover:bg-sheet group flex flex-col gap-2 overflow-hidden rounded-xl p-3"
            onClick={handleClick}
            data-testid="template-card"
          >
            <div
              className={twMerge(
                "border-0.5 border-border relative overflow-hidden rounded-lg",
                previewPlatform === "desktop"
                  ? "aspect-square"
                  : "aspect-[9/14]",
              )}
            >
              <ComponentTemplateSnapshot
                componentTemplate={template}
                previewPlatform={previewPlatform}
              />

              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-transparent to-black/20 opacity-0 transition-opacity duration-200 group-hover:opacity-100"></div>

              <div className="absolute right-2 top-2 flex gap-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                <CopyTemplateLinkButton template={template} />
              </div>
            </div>

            <div className="relative flex justify-between">
              <div className="flex max-w-[60%] flex-col items-start gap-1 overflow-hidden">
                <Tooltip triggerAsChild content={template.name} delay={300}>
                  <div className="w-full truncate text-sm font-medium">
                    {template.name}
                  </div>
                </Tooltip>
                <div className="text-muted min-h-4 text-xs">{industryName}</div>
              </div>
              {optimizedLogoUrl && (
                <img
                  src={optimizedLogoUrl}
                  alt={`${template.name} logo`}
                  className="absolute bottom-0 right-0 top-0 my-auto max-h-full w-auto max-w-[40%] object-contain"
                />
              )}
            </div>
          </button>
        </MenuTrigger>
      }
    />
  );
};

export default TemplateCard;
